import React from 'react';
import { getProductShot, type ProductShotKey } from '@/config/productShots';

interface ProductShotFigureProps {
  shot: ProductShotKey;
  /**
   * Set on the first shot above the fold so it is not lazy loaded. Everything
   * else stays lazy: these are screenshots, not content, and the marketing
   * pages are the ones the prerender optimises for first paint.
   */
  eager?: boolean;
  /** Hide the caption when the surrounding section already says the same thing. */
  showCaption?: boolean;
  className?: string;
}

/**
 * One back-office screenshot, or nothing at all.
 *
 * Renders null when the key has no committed image yet (see
 * src/config/productShots.ts), so a marketing page can never ship a broken
 * image. Width and height are always emitted so the browser reserves the box
 * before the file arrives and layout does not shift.
 */
const ProductShotFigure: React.FC<ProductShotFigureProps> = ({
  shot,
  eager = false,
  showCaption = true,
  className = '',
}) => {
  const image = getProductShot(shot);
  if (!image) return null;

  return (
    <figure className={`m-0 ${className}`}>
      <img
        src={image.src}
        alt={image.alt}
        width={image.width}
        height={image.height}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        className="w-full h-auto rounded-lg border border-border shadow-sm bg-card"
      />
      {showCaption && (
        <figcaption className="mt-3 text-sm text-muted-foreground">{image.caption}</figcaption>
      )}
    </figure>
  );
};

export default ProductShotFigure;
